import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { INDIAN_STATES } from 'src/app/private/constants/indian-states';
import { AutoCompleteContactSearchComponent } from 'src/app/private/modules/components/auto-complete-contact-search/auto-complete-contact-search.component';
import { AutoCompleteSearchComponent } from 'src/app/private/modules/components/auto-complete-search/auto-complete-search.component';
import { InvoiceService } from 'src/app/private/services/invoice.service';
import { DealService } from 'src/app/private/services/deals.service';
import { CompanyService } from 'src/app/private/services/company.service';
import { ProductService } from 'src/app/private/services/product.service';
import { CategoryService } from 'src/app/private/services/category.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { ManageBusinessInfoService } from 'src/app/private/services/manage-business-info.service';

@Component({
  selector: 'app-create-invoice',
  templateUrl: './create-invoice.component.html',
  styleUrls: ['./create-invoice.component.scss']
})
export class CreateInvoiceComponent implements OnInit {
  @Input() formAction: string = 'Add';
  @Input() editData: any = {};
  @Input() users: any[] = [];
  @Output() close: EventEmitter<any> = new EventEmitter();
  @Input() deals: any[] = [];
  @Output() reload: EventEmitter<any> = new EventEmitter();

  btnLoading = false;
  sendEmailLoading = false;
  accounts: any[] = [];
  invoice: any = {
    invoice_number: '',
    assign_to: this.localStorage.getUserId(),
    status: 'draft',
    invoice_date: new Date().toISOString().split('T')[0],
    subject: '',
    terms_conditions: '',
    due_date: null,
    currency: 'USD',
    subtotal: 0,
    tax_amount: 0,
    discount_amount: 0,
    total_amount: 0,
    notes: '',
    terms: {
      company_name: '',
      company_address: '',
      gst_number: ''
    },
    created_by: null,
    account_id: null,
    deal_id: null,
    deal_name: '',
    account_name: '',
    client_id: null,
    address: {
      billingStreet: '',
      billingCity: '',
      billingState: '',
      /** GST state code (2-digit), from billing state dropdown — stored in invoice.address JSON */
      billingStateCode: '',
      billingCode: '',
      billingCountry: '',
      shippingStreet: '',
      shippingCity: '',
      shippingState: '',
      shippingStateCode: '',
      shippingCode: '',
      shippingCountry: ''
    },
    items: [
      {
        category_id: '',
        product_id: '',
        productName: '',
        quantity: 1,
        unit_price: 0,
        amount: 0,
        discount_rate: 0,
        tax_rate: 0,
        total_price: 0,
        description: '',
        products: [],
        loadingProducts: false
      }
    ]
  };

  currencies = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'];

  /** Same list as manage-business-info / quote (GST state codes). */
  indianStates = INDIAN_STATES;

  shippingFields = [
    { label: 'Shipping Street', model: 'shippingStreet' },
    { label: 'Shipping City', model: 'shippingCity' },
    { label: 'Shipping State', model: 'shippingState' },
    { label: 'Shipping Code', model: 'shippingCode' },
    { label: 'Shipping Country', model: 'shippingCountry' }
  ];

  /** Subtotal + discount; Total Tax / GST split / Total Amount are explicit in the template. */
  totalsBeforeTax = [
    { label: 'Sub Total', model: 'subtotal' },
    { label: 'Discount', model: 'discount_amount' }
  ];

  /** Client GST state code from business profile (vs billing state for CGST+SGST vs IGST display). */
  clientGstStateCode = '';

  currencyCode: any = 'INR';

  @ViewChild('autoCompleteAccount') autoCompleteAccount!: AutoCompleteSearchComponent;
  @ViewChild('autoCompleteContact') autoCompleteContact!: AutoCompleteContactSearchComponent;

  isAdmin: boolean = false;
  formSubmitted: boolean = false;

  today = new Date().toISOString().split('T')[0];

  products: any[] = [];
  categories: any[] = [];

  constructor(
    private api: InvoiceService,
    private dealService: DealService,
    private companyService: CompanyService,
    private productService: ProductService,
    private categoryService: CategoryService,
    private toast: ToastrService,
    private localStorage: LocalStorageService,
    private tracker: TrackingService,
    private manageBusinessInfo: ManageBusinessInfoService
  ) {
    // Set current user as created_by
    this.invoice.created_by = this.localStorage.getUserId();
    this.invoice.client_id = this.localStorage.getClientId();

    this.isAdmin = this.localStorage.checkFullAccessForTableList(
      this.localStorage.getRole()
    );
  }


  ngOnInit() {
    this.currencyCode = this.localStorage.getClientCurrency();
    this.loadClientGstStateCode();

    // First load categories
    this.loadCategories();
    
    // Then load products
    this.loadProducts().then(() => {
      // After products are loaded, populate edit data if in update mode
      if (this.formAction === 'Update' && this.editData) {
        this.populateEditData();
      }
    });
    
    // Generate invoice number only for new invoices
    if (this.formAction === 'Add') {
      this.generateInvoiceNumber();
    }
  }

  private loadClientGstStateCode(): void {
    const clientId = this.localStorage.getClientId();
    if (!clientId) {
      return;
    }
    this.manageBusinessInfo.getClient({ client_id: clientId }).subscribe({
      next: (res: any) => {
        const d = res?.data ?? res;
        this.clientGstStateCode = String(d?.gst_state_code ?? '').trim();
      },
      error: () => {
        this.clientGstStateCode = '';
      }
    });
  }

  normalizeGstStateCode(code: string | null | undefined): string {
    const s = String(code ?? '').trim();
    if (!s) {
      return '';
    }
    const n = parseInt(s, 10);
    if (!Number.isFinite(n) || n < 0) {
      return s;
    }
    return n.toString().padStart(2, '0');
  }

  canShowGstSplit(): boolean {
    return !!this.normalizeGstStateCode(this.clientGstStateCode)
      && !!this.normalizeGstStateCode(this.invoice.address?.billingStateCode);
  }

  isIntraStateGst(): boolean {
    return (
      this.normalizeGstStateCode(this.clientGstStateCode)
      === this.normalizeGstStateCode(this.invoice.address?.billingStateCode)
    );
  }

  getCgstDisplay(): number {
    const t = Number(this.invoice.tax_amount) || 0;
    return Math.round((t / 2) * 100) / 100;
  }

  getSgstDisplay(): number {
    const t = Number(this.invoice.tax_amount) || 0;
    const cgst = this.getCgstDisplay();
    return Math.round((t - cgst) * 100) / 100;
  }

  getIgstDisplay(): number {
    return Number(this.invoice.tax_amount) || 0;
  }

  onBillingStateChange(stateName: string): void {
    const match = this.indianStates.find((s) => s.name === stateName);
    this.invoice.address.billingStateCode = match ? match.code : '';
  }

  syncBillingStateCodeFromName(): void {
    const name = String(this.invoice.address?.billingState || '').trim();
    if (!name) {
      this.invoice.address.billingStateCode = '';
      return;
    }
    const match = this.indianStates.find((s) => s.name === name);
    this.invoice.address.billingStateCode = match
      ? match.code
      : this.invoice.address.billingStateCode || '';
  }

  hasValidAddress(): boolean {
    const a = this.invoice.address;
    const billingOk = [
      a.billingStreet,
      a.billingCity,
      a.billingState,
      a.billingCode,
      a.billingCountry
    ].every((v) => String(v ?? '').trim() !== '');
    const billingGstOk = String(a.billingStateCode ?? '').trim() !== '';
    return billingOk && billingGstOk;
  }

  loadCategories() {
    const params = {
      client_id: this.localStorage.getClientId()
    };

    this.categoryService.getAllCategory(params).subscribe({
      next: (response: any) => {
        if (response && Array.isArray(response)) {
          this.categories = response;
        } else {
          this.categories = [];
        }
      },
      error: (error: any) => {
        console.error('Error loading categories:', error);
        this.toast.error('Failed to load categories');
      }
    });
  }

  onCategorySelectedForRow(event: any, rowIndex: number) {
    const categoryId = event.target.value;
    const item = this.invoice.items[rowIndex];
    
    // Reset all row data when category changes
    item.category_id = categoryId;
    item.product_id = '';
    item.productName = '';
    item.quantity = 1;
    item.unit_price = 0;
    item.amount = 0;
    item.discount_rate = 0;
    item.tax_rate = 0;
    item.total_price = 0;
    item.description = '';
    item.products = [];
    
    if (categoryId) {
      this.loadProductsForRow(categoryId, rowIndex);
    }
    
    // Recalculate totals after resetting the row
    this.updateTotals();
  }

  loadProductsForRow(categoryId: string, rowIndex: number) {
    const item = this.invoice.items[rowIndex];
    item.loadingProducts = true;
    
    const params: any = {
      "page": 0,
      "limit": 10000,
      where: {
        client_id: this.localStorage.getClientId(),
        cat_id: categoryId
      },
      "order": {
        "col": "\"product\".\"updatedAt\"",
        "dir": "DESC"
      }
    };

    this.productService.getAllProduct(params).subscribe({
      next: (response: any) => {
        item.loadingProducts = false;
        if (response && response.rows) {
          
          item.products = response.rows.map((p: any) => ({
            product_id: p.product_id,
            product_name: p.product_name,
            price: parseFloat(p.unit_price),
            description: p.description,
            tax : p.tax
          }));


        } else {
          item.products = [];
        }
      },
      error: (error: any) => {
        console.error('Error loading products:', error);
        item.loadingProducts = false;
        item.products = [];
        this.toast.error('Failed to load products');
      }
    });
  }

  loadProducts(): Promise<void> {
    const params: any = {
      "page": 0,
      "limit": 10000,
      where: {
        client_id: this.localStorage.getClientId(),
      },
      "order": {
        "col": "\"product\".\"updatedAt\"",
        "dir": "DESC"
      }
    };

    return new Promise((resolve, reject) => {
      this.productService.getAllProduct(params).subscribe({
        next: (response: any) => {
          if (response && response.rows) {
            this.products = response.rows.map((p: any) => ({
              product_id: p.product_id,
              product_name: p.product_name,
              price: parseFloat(p.unit_price),
              description: p.description,
              tax : p.tax
            }));
            resolve();
          } else {
            reject('No products found in response');
          }
        },
        error: (error: any) => {
          console.error('Error loading products:', error);
          this.toast.error('Failed to load products');
          reject(error);
        }
      });
    });
  }



  loadAccounts() {
    const params = {
      client_id: this.localStorage.getClientId(),
      page: 1,
      limit: 1000
    };

    this.companyService.getAllAccount(params).subscribe({
      next: (response: any) => {
        if (response && response.accounts) {
          this.accounts = response.accounts;
        }
      },
      error: (error: any) => {
        console.error('Error loading accounts:', error);
        this.toast.error('Failed to load accounts');
      }
    });
  }

  onDealSelected(event: any) {
    const dealId = event.target.value;
    const selectedDeal = this.deals.find(deal => deal.deal_id == dealId);
    if (selectedDeal) {
      this.invoice.deal_id = selectedDeal.deal_id;
      this.invoice.deal_name = selectedDeal.deal_name;
    } else {
      this.invoice.deal_id = null;
      this.invoice.deal_name = '';
    }
  }

  onAccountSelectedFromDropdown(event: any) {
    const accountId = event.target.value;
    const selectedAccount = this.accounts.find(account => account.account_id == accountId);
    if (selectedAccount) {
      this.invoice.account_id = selectedAccount.account_id;
      this.invoice.account_name = selectedAccount.account_name;
    } else {
      this.invoice.account_id = null;
      this.invoice.account_name = '';
    }
  }

  populateEditData() {
    // Map invoice items from invoice_item to match the component's format
    const mappedItems = this.editData.invoice_item?.map((item: any) => ({
      category_id: item.cat_id || '',
      product_id: item.product_id,
      productName: '',
      quantity: item.quantity,
      unit_price: item.price,
      amount: item.price * item.quantity,
      discount_rate: item.discount,
      tax_rate: item.tax,
      total_price: item.total,
      description: item.description,
      products: [],
      loadingProducts: false
    })) || [this.invoice.items[0]];

    const defaultAddr = {
      billingStreet: '',
      billingCity: '',
      billingState: '',
      billingStateCode: '',
      billingCode: '',
      billingCountry: '',
      shippingStreet: '',
      shippingCity: '',
      shippingState: '',
      shippingStateCode: '',
      shippingCode: '',
      shippingCountry: ''
    };

    // Prepare the invoice object with all fields from editData
    this.invoice = {
      ...this.invoice, // Keep default structure
      invoice_number: this.editData.invoice_number,
      invoice_date: this.editData.issue_date,
      due_date: this.editData.due_date ? new Date(this.editData.due_date).toISOString().split('T')[0] : '',
      status: this.editData.status,
      subtotal: this.editData.subtotal,
      tax_amount: this.editData.tax,
      discount_amount: this.editData.discount,
      total_amount: this.editData.total,
      paid_amount: this.editData.paid_amount || 0,
      notes: this.editData.notes || '',
      terms: this.editData.terms || this.invoice.terms,
      address: { ...defaultAddr, ...(this.editData.address || this.invoice.address || {}) },
      items: mappedItems,
      // Account details
      account_id: this.editData.account?.account_id,
      account_name: this.editData.account?.account_name,
      client_id: this.editData.client_id
    };

    this.syncBillingStateCodeFromName();

    // Load products for each item that has a category
    this.invoice.items.forEach((item: any, index: number) => {
      if (item.category_id) {
        this.loadProductsForRow(item.category_id, index);
      }
    });
  }

  generateInvoiceNumber() {
    if (this.formAction === 'Add') {
      const timestamp = Date.now().toString().slice(-6);
      this.invoice.invoice_number = `IV${timestamp}`;
    }
  }

  addRow() {
    this.invoice.items.push({
      category_id: '',
      product_id: '',
      productName: '',
      quantity: 1,
      unit_price: 0,
      listPrice: 0,
      amount: 0,
      discount_rate: 0,
      tax_rate: 0,
      total_price: 0,
      description: '',
      products: [],
      loadingProducts: false
    });
  }

  removeRow(index: number) {
    if (this.invoice.items.length > 1) {
      this.invoice.items.splice(index, 1);
      this.updateTotals();
    }
  }

  copyAddress() {
    this.invoice.address.shippingStreet = this.invoice.address.billingStreet;
    this.invoice.address.shippingCity = this.invoice.address.billingCity;
    this.invoice.address.shippingState = this.invoice.address.billingState;
    this.invoice.address.shippingStateCode = this.invoice.address.billingStateCode;
    this.invoice.address.shippingCode = this.invoice.address.billingCode;
    this.invoice.address.shippingCountry = this.invoice.address.billingCountry;

    this.toast.success('Address copied to shipping address');
  }

  updateTotals() {
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    this.invoice.items.forEach((item: any) => {
      // Ensure numeric values
      item.quantity = Number(item.quantity) || 0;
      item.unit_price = Number(item.unit_price) || 0;
      item.discount_rate = Number(item.discount_rate) || 0;
      item.tax_rate = Number(item.tax_rate) || 0;

      // Calculate row totals
      item.amount = item.quantity * item.unit_price;
      const discountAmount = (item.amount * item.discount_rate) / 100;
      const taxAmount = ((item.amount - discountAmount) * item.tax_rate) / 100;
      item.total_price = Number((item.amount - discountAmount + taxAmount).toFixed(2));

      // Add to invoice totals
      subtotal += item.amount;
      totalTax += taxAmount;
      totalDiscount += discountAmount;
    });

    // Update invoice totals
    this.invoice.subtotal = Number(subtotal.toFixed(2));
    this.invoice.discount_amount = Number(totalDiscount.toFixed(2));
    this.invoice.tax_amount = Number(totalTax.toFixed(2));
    this.invoice.total_amount = Number((subtotal - totalDiscount + totalTax).toFixed(2));
  }

  onAccountSelected(account: any) {
    this.invoice.account_id = account?.account_id;
    this.invoice.account_name = account?.account_name;
  }

  onContactSelected(contact: any) {
    this.invoice.client_id = contact?.id;
  }

  onProductSelected(event: any, index: number) {
    const productId = event.target.value;
    const item = this.invoice.items[index];
    const selectedProduct = item.products.find((p: any) => p.product_id == productId);
    if (selectedProduct) {
      console.log('Selected Product:', selectedProduct);
      
      // Calculate total tax percentage from product's tax array
      let totalTaxRate = 0;
      if (selectedProduct.tax && Array.isArray(selectedProduct.tax)) {
        console.log('Tax Array:', selectedProduct.tax);
        totalTaxRate = selectedProduct.tax.reduce((sum: number, taxItem: any) => {
          // Extract percentage value from item_text (e.g., "CGST - 9.00%" -> 9.00)
          if (taxItem.item_text) {
            const match = taxItem.item_text.match(/(\d+(?:\.\d+)?)\s*%/);
            if (match && match[1]) {
              const taxValue = parseFloat(match[1]);
              console.log(`Extracted tax from "${taxItem.item_text}": ${taxValue}%`);
              return sum + taxValue;
            }
          }
          return sum;
        }, 0);
      }
      
      console.log('Total Tax Rate Calculated:', totalTaxRate);

      this.invoice.items[index] = {
        ...this.invoice.items[index],
        product_id: selectedProduct.product_id,
        productName: selectedProduct.product_name,
        unit_price: selectedProduct.price || 0,
        description: selectedProduct.description || '',
        quantity: this.invoice.items[index].quantity || 1,
        discount_rate: this.invoice.items[index].discount_rate || 0,
        tax_rate: totalTaxRate
      };
      
      console.log('Updated Item:', this.invoice.items[index]);
      this.updateTotals();
    }
  }

  hasValidItems(): boolean {
    return this.invoice.items && this.invoice.items.length > 0 &&
      this.invoice.items.some((item: any) => item.product_id && item.product_id !== '');
  }

  validateForm(form: NgForm): boolean {
    // Mark form as submitted to trigger error message display
    Object.keys(form.controls).forEach(key => {
      form.controls[key].markAsTouched();
    });
    
    if (!form.valid) {
      return false;
    }

    if (!this.invoice.account_id) {
      return false;
    }

    if (!this.hasValidItems()) {
      return false;
    }

    if (!this.hasValidAddress()) {
      return false;
    }

    return true;
  }

  async saveAndSendInvoice(form: NgForm) {
    // Mark form as submitted to show validation errors
    this.formSubmitted = true;
    form.form.markAllAsTouched();
    
    if (!this.validateForm(form)) {
      this.toast.error('Please fill all required fields and add at least one item!');
      return;
    }

    const emailProviderType = this.localStorage.getEmailProviderTypeForPayload();
    const fromEmail = this.localStorage.getSendEmailFromAddress();
    if (!emailProviderType || !fromEmail) {
      this.toast.error(
        'Set an email preference or connect an Outlook/Gmail account before using Save & Send Email'
      );
      return;
    }

    this.sendEmailLoading = true;

    try {
      let res: any = await this.saveInvoice(form, true);
      console.log('Invoice saved, response:', res);

      

      console.log('Parsed invoice data:', res?.data?.id);

      // Placeholder email data - adapt as needed for actual API
      const emailData = {
        id: this.formAction === 'Add' ? res?.data?.id : this.editData.id,
        client_id: this.localStorage.getClientId(),
        companyName: this.invoice.account_name || this.editData?.account?.account_name,
        emailProviderType,
        from_email: fromEmail,
      };

      // attempt to send invoice if api has method

      this.api.sendInvoice(emailData).subscribe({
        next: (r: any) => {
          this.toast.success('Invoice Created and sent successfully!');
          this.reload.emit();
          this.sendEmailLoading = false;
          this.closeModal();
        },
        error: (error: any) => {
          console.error('Error sending invoice:', error);
          this.toast.error('Error sending invoice email. Please try again.');
        },
        complete: () => {
          this.btnLoading = false;
        }
      });

    } catch (error) {
      console.error('Error in save and send process:', error);
      this.toast.error('Error in save and send process. Please try again.');
      this.btnLoading = false;
    }
  }

  saveInvoice(form: NgForm, skipClose: boolean = false) {
    // Mark form as submitted to show validation errors
    this.formSubmitted = true;
    form.form.markAllAsTouched();
    
    if (!this.validateForm(form)) {
      this.toast.error('Please fill all required fields and add at least one item!');
      return Promise.reject('Form validation failed');
    }

    if (this.sendEmailLoading === false) {
      this.btnLoading = true;
    }

    // Build payload matching the expected API shape provided by the user
    const invoiceData = {
      client_id: this.invoice.client_id || this.localStorage.getClientId(),
      account_id: this.invoice.account_id || null,
      issue_date: this.invoice.invoice_date, // keep as YYYY-MM-DD string
      due_date: this.invoice.due_date, // keep as YYYY-MM-DD string
      status: this.invoice.status || 'unpaid',
      subtotal: this.invoice.subtotal || 0,
      discount: this.invoice.discount_amount || 0,
      tax: this.invoice.tax_amount || 0,
      total: this.invoice.total_amount || 0,
      paid_amount: this.invoice.paid_amount || 0,
      balance_due: (this.invoice.total_amount || 0) - (this.invoice.paid_amount || 0),
      notes: this.invoice.notes || '',
      subject : this.invoice.subject || '',
      terms: {
        company_name: this.invoice.terms?.company_name || '',
        company_address: this.invoice.terms?.company_address || '',
        gst_number: this.invoice.terms?.gst_number || ''
      },
      items: (this.invoice.items || []).map((item: any) => ({
        cat_id: item.category_id,
        product_id: item.product_id,
        description: item.description,
        name : item.productName,
        quantity: item.quantity,
        price: item.unit_price,
        tax: item.tax_rate,
        discount: item.discount_rate,
        total: item.total_price
      })),
      address: this.invoice.address || {},
    };

    if (this.formAction === 'Add') {
      return new Promise((resolve, reject) => {
        this.api.createInvoice(invoiceData).subscribe({
          next: (res: any) => {
            const desc = `Invoice created with number: ${this.invoice.invoice_number}`;
            this.tracker.track("Create", "Invoice", desc);

            if (!skipClose) {
              this.btnLoading = false;
              this.reload.emit();
              this.closeModal();
              this.toast.success('Invoice Created Successfully!');
            }
            resolve(res);
          },
          error: (error: any) => {
            console.error('Error creating invoice:', error);
            this.toast.error('Error creating invoice. Please try again.');
            this.btnLoading = false;
            reject(error);
          }
        });
      });
    } else {
      const updateData = {
        ...invoiceData,
        id: this.editData.id,
        client_id: this.localStorage.getClientId(),
        status: 'draft',
        user_id: this.localStorage.getUserId()
      };

      return new Promise((resolve, reject) => {
        this.api.updateInvoice(updateData).subscribe({
          next: (res: any) => {
            this.toast.success('Invoice Updated Successfully!');

            const desc = `Invoice updated with number: ${this.invoice.invoice_number}`;
            this.tracker.track("Update", "Invoice", desc);

            this.reload.emit();
            if (!skipClose) {
              this.closeModal();
            }
            this.btnLoading = false;
            resolve(res);
          },
          error: (error: any) => {
            console.error('Error updating invoice:', error);
            this.toast.error('Error updating invoice. Please try again.');
            this.btnLoading = false;
            reject(error);
          }
        });
      });
    }
  }

  closeModal(): void {
    this.close.emit();
  }

}
